import { useRouter } from "next/router";
import { createContext, PropsWithChildren, useContext, useEffect } from "react";
import { useMemo, useState } from "react";
import { useUser } from "@/auth/useUser";
import { Provider } from "@/components/common/providerSection";
import { useAppointmentBasicInfoQuery } from "@/graphql/queries/appointment/appointmentBasicInfo.graphql.types";
import { useServicesForBookingAppointmentQuery } from "@/graphql/queries/appointment/servicesForBookingAppointment.graphql.types";
import { useMedspaCommunicationSettingsQuery } from "@/graphql/queries/medspaCommunicationSettings.graphql.types";
import { useMedspaProvidersQuery } from "@/graphql/queries/medspaProviders.graphql.types";
import { useAppointmentServicesToFormInput } from "@/hooks/appointments/useAppointmentServicesToFormInput";
import useApptCustomizedCommunication from "@/hooks/appointments/useApptCustomizedCommunication";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";
import { useGetCreateAppointmentDefaultDate } from "@/hooks/dates/useCreateAppointmentDefaultDate";
import { useEffectOnce } from "@/hooks/misc/useEffectOnce";
import { AppointmentStatus, TelehealthType } from "@/types";
import { getUniqServicesProviders } from "@/utils/appointment";
import { ClientForAppointmentCreation } from "../../common/clientSection";
import { AvailableService } from "./appointmentServicesSection";

export type AppointmentServiceInput = {
  id?: string;
  serviceMenuItem: AvailableService;
  provider: Provider;
  startTime: Date;
  endTime: Date;
};

export type UseMultiProviderAppointmentDrawerProps = {
  calendarClickDate?: string;
  appointmentId?: string;
  isRebooking?: boolean;
  isEditing?: boolean;
  open: boolean;
};

const useMultiProviderAppointmentDrawer = ({
  calendarClickDate,
  appointmentId: _appointmentId,
  isRebooking: _isRebooking,
  isEditing: _isEditing,
  open,
}: UseMultiProviderAppointmentDrawerProps) => {
  const {
    query: { slug },
  } = useRouter();

  const appointmentId =
    _appointmentId || (typeof slug === "string" ? slug : undefined);
  const isRebooking = !!appointmentId && _isRebooking;
  const isEditing = !!appointmentId && _isEditing;

  const { utcToMedspaZonedTime, addTimezoneOffset } = useMedspaTimezone();
  const getDefaultDate = useGetCreateAppointmentDefaultDate();
  const { medspa } = useUser();

  const { data: providersData } = useMedspaProvidersQuery({
    variables: {
      id: medspa,
    },
    skip: !medspa,
  });
  const availableProviders = useMemo(
    () => providersData?.medspaByPk?.userMedspas || [],
    [providersData]
  );

  const [client, setClient] = useState<ClientForAppointmentCreation | null>(
    null
  );
  const [selectedProviders, setProviders] = useState<Provider[]>([]);
  const [appointmentServices, setAppointmentServices] = useState<
    AppointmentServiceInput[]
  >([]);

  const appointmentServicesProviders = useMemo(
    () => getUniqServicesProviders(appointmentServices),
    [appointmentServices]
  );

  const handleSetAppointmentServices = (
    appointmentServices: AppointmentServiceInput[]
  ) => {
    setAppointmentServices(appointmentServices);
    if (
      appointmentServices.length > 0 &&
      appointmentServices[0].startTime !== startDate
    ) {
      setStartDate(appointmentServices[0].startTime);
    }
  };

  const getInitialStartDate = () => {
    if (calendarClickDate) {
      const withTimeZoneOffset = addTimezoneOffset(new Date(calendarClickDate));

      return utcToMedspaZonedTime(withTimeZoneOffset);
    }

    return getDefaultDate();
  };

  /**
   * Start date of the appointment, used to store the date for the calendar
   * Works as initial startTime for the first added appointment service
   */
  const [startDate, setStartDate] = useState<Date | null>(getInitialStartDate);

  const resetState = () => {
    setClient(null);
    setProviders([]);
    handleSetAppointmentServices([]);
  };

  useEffect(() => {
    if (open) {
      setStartDate(getInitialStartDate());
    } else {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [telehealthType, setTelehealthType] = useState<TelehealthType | null>(
    null
  );
  const [telehealthVideoLink, setTelehealthVideoLink] = useState<string | null>(
    null
  );
  const [telehealthAdditionalDetails, setTelehealthAdditionalDetails] =
    useState<string | null>(null);

  const [
    isTelehealthAppointmentDetailsValid,
    setIsTelehealthAppointmentDetailsValid,
  ] = useState<boolean>(false); // managed by form component, has to be accessible by top level component

  const { data: appointmentData } = useAppointmentBasicInfoQuery({
    variables: {
      id: appointmentId as string,
      medspaId: medspa as string,
    },
    skip: !open || !appointmentId || !medspa,
    onCompleted: (data) => setClient(data.appointmentByPk.client),
  });

  const { mapAppointmentServicesToFormInput } =
    useAppointmentServicesToFormInput();

  const { data: servicesData } = useServicesForBookingAppointmentQuery({
    variables: {
      medspaId: medspa,
      clientId: client?.id,
    },
    fetchPolicy: "cache-and-network",
    skip: !medspa || !client?.id,
  });

  const availableServices = useMemo(
    () => servicesData?.medspaServiceMenuItem ?? [],
    [servicesData]
  );

  useEffectOnce(
    appointmentData?.appointmentByPk &&
      availableServices.length > 0 &&
      availableProviders.length > 0,
    () => {
      const appointmentServices = mapAppointmentServicesToFormInput({
        appointmentServices:
          appointmentData.appointmentByPk.appointmentServices,
        availableServices,
        availableProviders,
        isRebooking,
      });

      handleSetAppointmentServices(appointmentServices);
    }
  );

  const { data: medspaCommunicationSettingsData } =
    useMedspaCommunicationSettingsQuery({
      variables: {
        medspaId: medspa,
      },
      skip: !medspa,
    });

  const {
    apptCommunicationState,
    setApptCommunicationState,
    configurationToUpdate,
    communicationStatusText,
  } = useApptCustomizedCommunication(
    medspaCommunicationSettingsData?.medspaByPk.configuration
  );

  const initialAppointment = useMemo(() => {
    if (
      !appointmentData?.appointmentByPk ||
      !availableProviders.length ||
      !availableServices.length
    ) {
      return null;
    }

    const appointmentServices = mapAppointmentServicesToFormInput({
      appointmentServices: appointmentData.appointmentByPk.appointmentServices,
      availableServices,
      availableProviders,
      isRebooking,
    });

    return {
      id: appointmentData.appointmentByPk.id,
      status: appointmentData.appointmentByPk.status as AppointmentStatus,
      appointmentServices,
    };
  }, [
    appointmentData?.appointmentByPk,
    availableProviders,
    availableServices,
    isRebooking,
    mapAppointmentServicesToFormInput,
  ]);

  return {
    appointmentId,
    client,
    setClient,
    availableServices,
    startDate,
    availableProviders,
    selectedProviders,
    setProviders,
    appointmentServices,
    appointmentServicesProviders,
    setAppointmentServices: handleSetAppointmentServices,
    isRebooking,
    isEditing,

    telehealthType,
    setTelehealthType,
    telehealthVideoLink,
    setTelehealthVideoLink,
    telehealthAdditionalDetails,
    setTelehealthAdditionalDetails,
    isTelehealthAppointmentDetailsValid,
    setIsTelehealthAppointmentDetailsValid,

    apptCommunicationState,
    setApptCommunicationState,
    configurationToUpdate,
    communicationStatusText,

    initialAppointment,
  };
};

type MultiProviderAppointmentDrawerContextType = ReturnType<
  typeof useMultiProviderAppointmentDrawer
>;

const MultiProviderAppointmentDrawerContext = createContext<
  MultiProviderAppointmentDrawerContextType | undefined
>(undefined);

export const MultiProviderAppointmentDrawerContextProvider = ({
  children,
  calendarClickDate,
  appointmentId,
  open,
  isRebooking,
  isEditing,
}: PropsWithChildren & {
  calendarClickDate?: string;
  appointmentId?: string;
  open: boolean;
  isRebooking?: boolean;
  isEditing?: boolean;
}) => {
  const value = useMultiProviderAppointmentDrawer({
    calendarClickDate,
    appointmentId,
    open,
    isRebooking,
    isEditing,
  });

  return (
    <MultiProviderAppointmentDrawerContext.Provider value={value}>
      {children}
    </MultiProviderAppointmentDrawerContext.Provider>
  );
};

export const useMultiProviderAppointmentDrawerContext = () => {
  const context = useContext(MultiProviderAppointmentDrawerContext);
  if (!context) {
    throw new Error(
      "useMultiProviderAppointmentDrawerContext must be used within a MultiProviderAppointmentDrawerContextProvider"
    );
  }

  return context;
};
